import React, { FunctionComponent, useCallback } from 'react';

import { ThemeName } from '@ps-int/general-themes';
import { useGeneralThemesDatasync } from '@ps-int/general-themes-components';
import { ImagePicker, ImagePickerProps } from '@yandex-360/orb-components/dist/components/ImagePicker';
import { ImagePickerItemProps } from '@yandex-360/orb-components/dist/components/ImagePicker/ImagePickerItem';
import { useSelector } from 'react-redux';

import { i18n } from '../../../../../../helpers/i18n';
import { countMetrika } from '../../../../../../helpers/metrika';
import { useActions } from '../../../../../../hooks';
import { setTheme as setThemeAction, setVideoBg as setVideoBgAction } from '../../../../../config';
import {
    getThemeName,
    isNewYearThemeAvailable as selectIsNewYearThemeAvailable,
} from '../../../../../config/selectors';
import { useGeneralThemesSettingsDatasyncService } from '../../../../hooks/useGeneralThemesSettingsDatasyncService';
import { Heading } from '../../heading';
import { Island } from '../../island';
import { SettingsTumbler } from '../../tumblers';
import css from './styles.css';

const { themes } = require('@ps-int/general-themes/themes');

export const ThemeSection: FunctionComponent = () => {
    const currentTheme = useSelector(getThemeName);
    const isNewYearThemeAvailable = useSelector(selectIsNewYearThemeAvailable);
    const { setStoreTheme, setVideoBg } = useActions({ setStoreTheme: setThemeAction, setVideoBg: setVideoBgAction });
    const ds = useGeneralThemesSettingsDatasyncService();
    const { isVideoBgEnabled, toggleVideoBg, setTheme: setDsTheme } = useGeneralThemesDatasync(ds);

    const availableThemes: ThemeName[] = [
        ThemeName.GeneralDefault,
        ...(isNewYearThemeAvailable ? [ThemeName.GeneralNewYear2024] : []),
        ThemeName.GeneralFlow,
        ThemeName.GeneralDarkflow,
        ThemeName.GeneralGrass,
    ];

    const setTheme = useCallback(
        async (theme: ThemeName) => {
            await setDsTheme(theme);
            setStoreTheme({ theme, settings: ds.getCommonSettings() });
        },
        [ds, setDsTheme, setStoreTheme]
    );

    const imageItems = availableThemes.reduce((items, theme) => {
        if (theme === ThemeName.GeneralDefault) {
            return items;
        }

        items.push({
            item: <img key={theme} src={themes[theme].thumbnail.webp} alt={theme} />,
            testId: `theme-${theme}`,
            value: theme,
        });

        return items;
    }, [] as ImagePickerItemProps[]);

    const onThemeChange = useCallback(
        (value: string) => {
            const theme = value === 'no-image' ? ThemeName.GeneralDefault : (value as ThemeName);
            countMetrika('interface elements', 'settings popup', 'change theme', theme);
            setTheme(theme);
        },
        [setTheme]
    );

    const onTumblerChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            toggleVideoBg();
            const checked = event.target.checked;
            setVideoBg(checked);
            countMetrika('interface elements', 'settings popup', 'videobackground', checked ? 'on' : 'off');
        },
        [toggleVideoBg, setVideoBg]
    );

    const themeDefaultValue = currentTheme === ThemeName.GeneralDefault || !currentTheme ? 'no-image' : currentTheme;

    return (
        <Island className={css.themeWrapper}>
            <Heading
                title={i18n('settings-v2', 'section-theme-title')}
                subtitle={i18n('settings-v2', 'section-theme-subtitle')}
            />
            <SettingsTumbler
                testId="video-background-button"
                label={i18n('settings-v2', 'video-background-tumbler-label')}
                checked={isVideoBgEnabled}
                onChange={onTumblerChange}
            />
            <div className={css.pickerWrapper}>
                <ImagePicker
                    testId="theme-picker"
                    position="fixed-stretch"
                    imageItems={imageItems}
                    defaultValue={themeDefaultValue}
                    onChange={onThemeChange as ImagePickerProps['onChange']}
                />
            </div>
        </Island>
    );
};
